/**
 * Sixty-six scoring and match end (docs/rules/sixty-six.md §7–§9).
 *
 * A hand ends the moment a player's count reaches 66 — they go out — or when
 * the cards run out. Its winner scores game points by how badly the other
 * player did: one, two if they have under 33 ("schneider"), three if they took
 * no trick at all ("schwarz"). A close changes the stakes: the closer who goes
 * out is scored the same way, but a closer who does not — the other player
 * gets there first, or the cards run out short — gives the other player two,
 * or three if they had not taken a trick when the talon was closed. A hand
 * played out to 65–65 is drawn and nobody scores.
 */
import { SUIT_LABEL } from '../../core/cards.js';
import { SIXTY_SIX_OUT, SIXTY_SIX_SCHNEIDER } from './config.js';
import { sixtySixCountOf, sixtySixOpponent } from './rules.js';
/** Rules §8: what the winner of a hand scores, from the loser's count. */
export function gamePointsFrom(loserTotal, loserTricks) {
    if (loserTricks === 0)
        return 3;
    if (loserTotal < SIXTY_SIX_SCHNEIDER)
        return 2;
    return 1;
}
/** Who went out: the seat whose count reached 66. Only one can — every check
 *  follows the one change that could have got a seat there — but if both
 *  somehow stand at 66, the winner of the last trick went out first. */
function outSeat(hand, count) {
    const reached = [0, 1].filter((s) => count.total[s] >= SIXTY_SIX_OUT);
    if (reached.length === 1)
        return reached[0];
    if (reached.length === 0)
        return null;
    const last = [...hand.tricks].reverse().find((t) => t.winner !== null);
    return last?.winner ?? reached[0];
}
export function sixtySixOutcome(hand, count = sixtySixCountOf(hand)) {
    const closed = hand.extra.closed;
    const out = outSeat(hand, count);
    const failedClose = (closer) => {
        const winner = sixtySixOpponent(closer);
        return closed.opponentHadTrick
            ? { kind: 'out', winner, points: 2, why: 'closed and fell short' }
            : { kind: 'out', winner, points: 3, why: 'closed against no tricks and fell short' };
    };
    if (closed) {
        if (out === closed.seat) {
            const loser = sixtySixOpponent(out);
            return { kind: 'out', winner: out, points: gamePointsFrom(count.total[loser], count.tricks[loser]), why: 'closed and went out' };
        }
        return failedClose(closed.seat);
    }
    if (out !== null) {
        const loser = sixtySixOpponent(out);
        return { kind: 'out', winner: out, points: gamePointsFrom(count.total[loser], count.tricks[loser]), why: 'went out' };
    }
    // Played out with nobody at 66: only 65–65 is possible, and it is drawn. A
    // higher count would still win, should a variant ever make one possible.
    if (count.total[0] === count.total[1])
        return { kind: 'drawn' };
    const winner = count.total[0] > count.total[1] ? 0 : 1;
    const loser = sixtySixOpponent(winner);
    return { kind: 'out', winner, points: gamePointsFrom(count.total[loser], count.tricks[loser]), why: 'had more' };
}
function describeLoss(points, total, tricks) {
    if (tricks === 0)
        return 'no tricks';
    return points === 2 ? `${total}, under 33` : `${total}`;
}
export function sixtySixScoreHand(hand, table) {
    const count = sixtySixCountOf(hand);
    const outcome = sixtySixOutcome(hand, count);
    const deltas = table.teams.map(() => 0);
    const lines = [];
    for (const seat of [0, 1]) {
        const bits = [`${count.cards[seat]} in cards`];
        if (count.marriages[seat] > 0) {
            bits.push(count.tricks[seat] > 0 ? `${count.marriages[seat]} in marriages` : `${count.marriages[seat]} in marriages, void without a trick`);
        }
        if (count.lastTrick[seat] > 0)
            bits.push('10 for the last trick');
        lines.push({ team: seat, text: `Counted ${count.total[seat]}: ${bits.join(', ')}`, points: 0 });
    }
    if (outcome.kind === 'out') {
        const loser = sixtySixOpponent(outcome.winner);
        deltas[outcome.winner] = outcome.points;
        const closed = hand.extra.closed;
        const text = closed && closed.seat !== outcome.winner
            ? (closed.opponentHadTrick ? 'The other side closed and fell short' : 'The other side closed against no tricks and fell short')
            : `${closed ? 'Closed and went out' : outcome.why === 'went out' ? 'Went out' : 'Had more'} — the other side ${describeLoss(outcome.points, count.total[loser], count.tricks[loser])}`;
        lines.push({ team: outcome.winner, text, points: outcome.points });
    }
    else {
        lines.push({ team: 0, text: 'Drawn at 65 each: nobody scores', points: 0 });
    }
    return {
        deltas,
        tricksBySeat: [count.tricks[0], count.tricks[1]],
        // A closed hand is the closer's contract: made or not. Otherwise there is
        // no contract to make.
        contractMade: hand.extra.closed && outcome.kind === 'out' ? outcome.winner === hand.extra.closed.seat : null,
        lines,
    };
}
/** Rules §9: first to the target. Only the hand's winner scores, so two
 *  players never reach it together. */
export function sixtySixMatchEnd(state, config) {
    const at = state.scores.findIndex((s) => s >= config.target);
    if (at === -1)
        return { finished: false };
    return { finished: true, winningTeam: at, reason: `${state.players[at]?.name ?? `Seat ${at + 1}`} reached ${config.target}` };
}
/** The deal: winner of the hand deals the next; a drawn hand is dealt again
 *  by the same dealer (rules §2). */
export function sixtySixNextDealer(state) {
    const deltas = state.hand?.result?.deltas ?? [];
    const winner = deltas.findIndex((d) => d > 0);
    return winner === -1 ? state.dealer : winner;
}
export function sixtySixContractLabel(contract, extra) {
    if (!contract)
        return '—';
    return `${SUIT_LABEL[contract.trump]}${extra?.closed ? ' closed' : ''}`;
}
//# sourceMappingURL=scoring.js.map